import { usePermissionsStore } from "../permissions/permissions_store";
import { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useCookies } from "react-cookie";

import { AppContexts } from "../../providers";

import RecordsPerPage from "../common/RecordsPerPage";
import Pagination from "../common/Pagination";

import AddEdit from "./components/add_edit.js";
import Remove from "./components/remove.js";

import { TextFilter } from "../common/Filters";

const Zones = () => {
  const { canEdit } = usePermissionsStore();
  const canManageZones = canEdit("admin.zones");
  const {
    loading,
    zones,
    zonesCount,
    getZones,
    createZone,
    editZone,
    removeZone,
    toggleZoneItem,
  } = useContext(AppContexts.ZonesContext);

  const {
    get: getInbox,
    settings: inboxSettings,
    toggleInboxRestriction,
  } = useContext(AppContexts.InboxContext);
  const { get: getRentals, rentals } = useContext(AppContexts.RentalsContext);

  const defaultFilters = {
    search: "",
    sort: "updated",
    page: 0,
    per_page: 20,
  };

  const [stale, setStale] = useState(false);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);

  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingProperties, setPendingProperties] = useState([]);
  const [pendingRestrictions, setPendingRestrictions] = useState({
    known: [],
    "ignore-webchat": [],
  });

  const [filters, setFilters] = useState({
    ...defaultFilters,
    ...Object.fromEntries(new URLSearchParams(window.location.search || {})),
  });

  const [cookies] = useCookies(["records-per-page"]);
  const { "records-per-page": recordsPerPage } = cookies;

  const inbox = useMemo(() => {
    return inboxSettings || null;
  }, [inboxSettings]);

  const inboxes = useMemo(() => {
    return (inbox?.recipients || [])
      .map((r) => {
        return (r.personal_inboxes || [])[0];
      })
      .filter((i) => !!i);
  }, [inbox]);

  useEffect(async () => {
    getRentals();
    getInbox();

    await getZones(filters);

    setStale(false);
  }, []);

  useEffect(() => {
    setFilters(() => ({ ...filters, per_page: recordsPerPage }));
  }, [recordsPerPage]);

  useEffect(async () => {
    console.log("Refreshing", stale);

    if (stale) {
      await getZones(filters);
      setStale(false);
    }
  }, [stale]);

  useEffect(() => {
    document.title = "Zones - RueBaRue";
  }, []);

  useEffect(() => {
    setStale(true);
  }, [filters]);

  const sortLabels = { updated: "Last Updated", label: "Name" };

  const openAdd = () => {
    setEditing({});
  };

  const openEdit = (item) => {
    setEditing(item);
  };

  const onSave = async () => {
    setStale(true);
    setEditing(null);
  };

  const openRemove = (item) => {
    setRemoving(item);
  };

  const onRemove = async () => {
    setStale(true);
    setRemoving(null);
  };

  const onClose = async () => {
    setEditing(null);
    setRemoving(null);
  };

  const updateFilter = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const getRecipientFromInbox = (personal_inbox_id) => {
    return (inbox?.recipients || []).find((r) => {
      return (r.personal_inboxes || []).find(
        (pi) => pi.personal_inbox_id === personal_inbox_id,
      );
    });
  };

  const buildZoneName = (item) => {
    return item.label;
  };

  const buildDescription = (item) => {
    return item.description;
  };

  const buildNumberofUsers = (item) => {
    return item.users_count || "-";
  };

  const buildNumberofHomeGuides = (item) => {
    return item.rentals_count || "-";
  };

  const buildNumberofContacts = (item) => {
    return item.contacts_count || "-";
  };

  const toggleUser = async (zone, type, item_id) => {
    if (!canManageZones) return;
    if ((pendingUsers[zone.id] || []).includes(item_id)) {
      return;
    }

    setPendingUsers({
      ...pendingUsers,
      [zone.id]: [...(pendingUsers[zone.id] || []), item_id],
    });

    await toggleZoneItem(zone, type, item_id);
    await getZones();

    setPendingUsers({
      ...pendingUsers,
      [zone.id]: [
        ...(pendingUsers[zone.id] || []).filter((id) => id !== item_id),
      ],
    });
  };

  const toggleRestriction = async (personal_inbox_id, token) => {
    if (!canManageZones) return;
    if (pendingRestrictions[token].includes(personal_inbox_id)) {
      return;
    }
    setPendingRestrictions({
      ...pendingRestrictions,
      [token]: [...pendingRestrictions[token], personal_inbox_id],
    });

    await toggleInboxRestriction(personal_inbox_id, token);
    await getInbox();

    setPendingRestrictions({
      ...pendingRestrictions,
      [token]: pendingRestrictions[token].filter((p) => p === token),
    });
  };

  return (
    <>
      <section id="message-routing">
        <div id="hms-page-title">
          <h1 className="uk-heading-small">Zones</h1>
        </div>
        <div id="hms-main-body">
          <div className="uk-flex uk-flex-between uk-flex-top">
            <div className="hms-guest-btn">
              {canManageZones && (<button
                className="uk-button hms-btn hms-red-btn"
                onClick={openAdd}
              >
                <span uk-icon="icon: plus; ratio: 0.7"></span> Zone
              </button>)}
            </div>
          </div>
          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
            <div>
              <div className="uk-flex uk-flex-between uk-flex-top guest-form-top-filters">
                <div>
                  <TextFilter
                    placeholder="Filter by Name"
                    value={filters.search}
                    set={(v) => {
                      setFilters({ ...filters, search: v });
                    }}
                  />
                </div>
                <div className="hms-sortby-btn">
                  <button className="uk-button" type="button">
                    SORT BY<span uk-icon="icon: triangle-down"></span>
                  </button>
                  <div uk-dropdown="mode: click; offset: 0; animation: uk-animation-slide-top-small; duration: 400">
                    <ul className="uk-nav uk-dropdown-nav">
                      {["updated", "label"].map((item) => (
                        <li
                          className={filters.sort === item ? "uk-active" : ""}
                          key={`sort_${item}`}
                        >
                          <a
                            href="#"
                            onClick={(e) => {
                              setFilters({
                                ...filters,
                                sort: item,
                              });
                            }}
                          >
                            {sortLabels[item]}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/*zones.length === 0 ? (
                <div className="uk-alert-primary uk-margin-small-top">
                  You have not created any yet.
                </div>
              ) : null*/}

              <p>Use Zones to restrict user's access to specific messages</p>
              <table className="uk-table uk-table-hover uk-table-divider hms-table faq-table">
                <thead>
                  <tr>
                    <th className="uk-table-expand">Zone Name</th>
                    <th className="uk-table-expand">Description</th>
                    <th className="uk-table-expand">Number of Users</th>
                    <th className="uk-table-expand">Number of Home Guides</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="uk-text-center">
                        <div uk-spinner=""></div>
                        Loading...
                      </td>
                    </tr>
                  ) : (
                    [
                      <tr key="known">
                        <td>
                          <b>Unknown Numbers</b>
                        </td>
                        <td>
                          Default Zone: Users will recieve messages from numbers
                          not in our system
                        </td>
                        <td className="hms-add-tag">
                          <div className="uk-flex uk-flex-top">
                            <div className="hms-add-tag">
                              <button
                                className="uk-button"
                                type="button"
                                aria-haspopup="true"
                                aria-expanded="false"
                              >
                                <img src="/images/group.svg" />
                              </button>
                              <div
                                data-uk-dropdown="mode: click"
                                className="guest-tags-dropdown uk-dropdown"
                              >
                                <ul className="uk-nav uk-dropdown-nav tags-selector">
                                  {(inboxes || []).map((i) => {
                                    let r = getRecipientFromInbox(
                                      i.personal_inbox_id,
                                    );

                                    if (!r) {
                                      return null;
                                    }

                                    return (
                                      <li>
                                        <label>
                                          <input
                                            className="uk-checkbox"
                                            type="checkbox"
                                            value={i.personal_inbox_id}
                                            disabled={!canManageZones || pendingRestrictions[
                                              "known"
                                            ].includes(i.personal_inbox_id)}
                                            checked={
                                              !i.restrictions.includes("known")
                                            }
                                            onChange={() =>
                                              toggleRestriction(
                                                i.personal_inbox_id,
                                                "known",
                                              )
                                            }
                                          />
                                          {`${r.first_name} ${r.last_name}`}
                                        </label>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            </div>
                            <div className="hms-guest-tags">
                              <div className="guest-tags">
                                {(inboxes || []).map((i) => {
                                  if (i.restrictions.includes("known")) {
                                    return null;
                                  } else {
                                    let r = getRecipientFromInbox(
                                      i.personal_inbox_id,
                                    );

                                    if (!r) {
                                      return null;
                                    }

                                    return (
                                      <div className="uk-inline inbox">
                                        {r.first_name} {r.last_name}
                                      </div>
                                    );
                                  }
                                })}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>N/A</td>
                        <td className="hms-action-btn"></td>
                      </tr>,

                      <tr key="ignore-webchat">
                        <td>
                          <b>Webchat Messages</b>
                        </td>
                        <td>
                          Default Zone: Users will recieve messages from the
                          embedded web chat
                        </td>
                        <td className="hms-add-tag">
                          <div className="uk-flex uk-flex-top">
                            <div className="hms-add-tag">
                              <button
                                className="uk-button"
                                type="button"
                                aria-haspopup="true"
                                aria-expanded="false"
                              >
                                <img src="/images/group.svg" />
                              </button>
                              <div
                                data-uk-dropdown="mode: click"
                                className="guest-tags-dropdown uk-dropdown"
                              >
                                <ul className="uk-nav uk-dropdown-nav tags-selector">
                                  {(inboxes || []).map((i) => {
                                    let r = getRecipientFromInbox(
                                      i.personal_inbox_id,
                                    );

                                    if (!r) {
                                      return null;
                                    }

                                    return (
                                      <li>
                                        <label>
                                          <input
                                            className="uk-checkbox"
                                            type="checkbox"
                                            value={i.personal_inbox_id}
                                            disabled={!canManageZones || pendingRestrictions[
                                              "ignore-webchat"
                                            ].includes(i.personal_inbox_id)}
                                            checked={
                                              !i.restrictions.includes(
                                                "ignore-webchat",
                                              )
                                            }
                                            onChange={() =>
                                              toggleRestriction(
                                                i.personal_inbox_id,
                                                "ignore-webchat",
                                              )
                                            }
                                          />
                                          {`${r.first_name} ${r.last_name}`}
                                        </label>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            </div>
                            <div className="hms-guest-tags">
                              <div className="guest-tags">
                                {(inboxes || []).map((i) => {
                                  if (
                                    i.restrictions.includes("ignore-webchat")
                                  ) {
                                    return null;
                                  } else {
                                    let r = getRecipientFromInbox(
                                      i.personal_inbox_id,
                                    );

                                    if (!r) {
                                      return null;
                                    }

                                    return (
                                      <div className="uk-inline inbox">
                                        {r.first_name} {r.last_name}
                                      </div>
                                    );
                                  }
                                })}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>N/A</td>
                        <td className="hms-action-btn"></td>
                      </tr>,

                      ...(zones || []).map((item) => {
                        return (
                          <tr key={item.id}>
                            <td>
                              <b>{buildZoneName(item)}</b>
                            </td>
                            <td>{buildDescription(item)}</td>
                            <td className="hms-add-tag">
                              <div className="uk-flex uk-flex-top">
                                <div className="hms-add-tag">
                                  <button
                                    className="uk-button"
                                    type="button"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                  >
                                    <img src="/images/group.svg" />
                                  </button>
                                  <div
                                    data-uk-dropdown="mode: click"
                                    className="guest-tags-dropdown uk-dropdown"
                                  >
                                    <ul className="uk-nav uk-dropdown-nav tags-selector">
                                      {(inboxes || []).map((i) => {
                                        let r = getRecipientFromInbox(
                                          i.personal_inbox_id,
                                        );

                                        if (!r) {
                                          return null;
                                        }

                                        return (
                                          <li>
                                            <label>
                                              <input
                                                className="uk-checkbox"
                                                type="checkbox"
                                                value={i.personal_inbox_id}
                                                disabled={!canManageZones || (
                                                  pendingUsers[item.id] || []
                                                ).includes(i.personal_inbox_id)}
                                                checked={(
                                                  item.users || []
                                                ).includes(i.personal_inbox_id)}
                                                onChange={() =>
                                                  toggleUser(
                                                    item,
                                                    "inbox",
                                                    i.personal_inbox_id,
                                                  )
                                                }
                                              />
                                              {`${r.first_name} ${r.last_name}`}
                                            </label>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  </div>
                                </div>
                                <div className="hms-guest-tags">
                                  <div className="guest-tags">
                                    {(inboxes || []).map((i) => {
                                      if (
                                        (item.users || []).includes(
                                          i.personal_inbox_id,
                                        )
                                      ) {
                                        let r = getRecipientFromInbox(
                                          i.personal_inbox_id,
                                        );

                                        if (!r || !item) {
                                          return null;
                                        }

                                        return (
                                          <div className="uk-inline inbox">
                                            {r.first_name} {r.last_name}
                                          </div>
                                        );
                                      } else {
                                        return null;
                                      }
                                    })}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>{buildNumberofHomeGuides(item)}</td>
                            <td className="hms-action-btn">
                              <div className="uk-flex uk-flex-top">
                                <div className="uk-inline">
                                  {canManageZones && (<button
                                    className="uk-button"
                                    uk-tooltip="Edit Rule"
                                    onClick={() => {
                                      openEdit(item);
                                    }}
                                  >
                                    <img
                                      data-uk-img
                                      loading="lazy"
                                      data-src="/images/edit.svg"
                                      src="/images/edit.svg"
                                    />
                                  </button>)}
                                </div>

                                <div className="uk-inline">
                                  {canManageZones && (<button
                                    className="uk-button"
                                    uk-tooltip="Delete Rule"
                                    onClick={() => {
                                      openRemove(item);
                                    }}
                                  >
                                    <img
                                      data-uk-img
                                      loading="lazy"
                                      data-src="/images/delete.svg"
                                      src="/images/delete.svg"
                                    />
                                  </button>)}
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      }),
                    ]
                  )}

                  {/*zones.length === 0 && (
                    <tr>
                      <td colSpan={6} className="uk-text-center">
                        No entries found
                      </td>
                    </tr>
                  )*/}
                </tbody>
              </table>
            </div>
          </div>

          <div className="uk-flex uk-flex-between uk-flex-top">
            {zonesCount > 0 && (
              <>
                <Pagination
                  count={zonesCount}
                  index={filters.page}
                  onIndexChanged={(page) => {
                    setFilters({ ...filters, page: page });
                  }}
                />
                <RecordsPerPage />
              </>
            )}
          </div>
        </div>
      </section>

      {canManageZones && (<AddEdit data={editing} onSuccess={onSave} onClose={onClose} />)}
      {canManageZones && (<Remove data={removing} onRemove={onRemove} onClose={onClose} />)}
    </>
  );
};

export default Zones;
